import React, { useEffect, useState, useContext } from 'react';
import { signOut } from 'aws-amplify/auth';
import PropTypes from 'prop-types';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import Primarytheme, { SERVICE_COLORS } from './theme';
import ServiceContext from './ServiceContext';
import { IconButton, ListItem, ButtonBase, Button, colors } from '@mui/material';
import { NavLink as RouterLink, useLocation, useHistory } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import Collapse from '@mui/material/Collapse';
import WarningIcon from '@mui/icons-material/Warning';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import InfoIcon from '@mui/icons-material/Info';
import HelpIcon from '@mui/icons-material/Help';
import EngineeringIcon from '@mui/icons-material/Engineering';

import release from './../../release-note.json';

import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';

import Popover from '@mui/material/Popover';
import Divider from '@mui/material/Divider';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BuildIcon from '@mui/icons-material/Build';
import CelebrationIcon from '@mui/icons-material/Celebration';
import ListItemLink from './ListItemLink';


const AppBar = styled(MuiAppBar)(({ theme }) => ({
  borderWidth: 0,
  borderBottomWidth: 1,
  borderStyle: 'solid',
  borderColor: (theme.vars ?? theme).palette.divider,
  boxShadow: 'none',
  zIndex: theme.zIndex.drawer + 1,
}));

const parseLinks = (text) => {
  if (!text) return text;
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const result = [];
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index));
    }
    result.push(<Link key={match.index} href={match[2]} target="_blank" rel="noopener">{match[1]}</Link>);
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }
  return result.length > 0 ? result : text;
};

const releaseIconMap = {
  'enhancement': <AutoAwesomeIcon color='warning' />,
  'bug': <BuildIcon color='neutral' />,
  'feature': <CelebrationIcon color='secondary' />,
  'wip': <EngineeringIcon color='primary' />
}

function DashboardHeader({ auth, logo, title, menuOpen, onToggleMenu }) {
  const theme = useTheme();
  const location = useLocation();
  const { activeService } = useContext(ServiceContext);
  const headerColor = (SERVICE_COLORS[activeService] || SERVICE_COLORS.pagination).main;

  const handleMenuOpen = React.useCallback(() => {
    onToggleMenu(!menuOpen);
  }, [menuOpen, onToggleMenu]);

  const getMenuIcon = React.useCallback(
    (isExpanded) => {
      const expandMenuActionText = 'Expand';
      const collapseMenuActionText = 'Collapse';

      return (
        <Tooltip
          title={`${isExpanded ? collapseMenuActionText : expandMenuActionText} menu`}
          enterDelay={1000}
        >
          <div>
            <IconButton
              size="small"
              color='white'
              aria-label={`${isExpanded ? collapseMenuActionText : expandMenuActionText} navigation menu`}
              onClick={handleMenuOpen}
            >
              {isExpanded ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>
          </div>
        </Tooltip>
      );
    },
    [handleMenuOpen],
  );

  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorInfoEl, setAnchorInfoEl] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleReleaseNote = () => {
    if (!dialogOpen) handleDialogOpen();
  }

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleInfoClick = (event) => {
    setAnchorInfoEl(event.currentTarget);
  };

  const handleInfoClose = () => {
    setAnchorInfoEl(null);
  };


  const handleLogOut = async () => {
    try {
      await signOut();
      handleClose();
      auth.setUserState({
        ...auth.userState,
        isAuthenticated: false,
        user: null,
        userItem: null,
      })
      localStorage.clear();
      sessionStorage.setItem("manualLogout", true)
    } catch (error) {
      console.error('Logout error');
    }
  }


  const history = useHistory();
  const redirect = (dst) => {
    history.push(dst);
  }

  const openPop = Boolean(anchorEl);
  const openInfoPop = Boolean(anchorInfoEl);
  //////// ALERT
  const [open, setOpen] = React.useState(true);

  return (
    <AppBar color="inherit" position="absolute" sx={{ displayPrint: 'none' }}>
      <Toolbar sx={{ backgroundColor: headerColor, mx: { xs: -0.75, sm: -1 } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            flexWrap: 'wrap',
            width: '100%',
          }}
        >
          <Stack direction="row" alignItems="center">
            <Box sx={{ mr: 1 }}>{getMenuIcon(menuOpen)}</Box>
            <Stack direction="row" alignItems="center">
              {logo ? <Button variant='text' onClick={() => redirect(activeService === 'labels' ? '/labels' : '/dashboard')} sx={{ color: '#ffffff', p: 0 }}>
                <img src={logo} height="30" alt="pagination logo" />
              </Button> : null}
            </Stack>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ marginLeft: 'auto' }}
          >
            {(auth.userState.isAuthenticated && auth.userState.custom_message &&
              <Collapse in={open} orientation={'horizontal'}>
                <Alert
                  sx={{ pl: "2em", pr: "2em" }}
                  icon={<WarningIcon fontSize="inherit" />}
                  variant="filled"
                  severity="error"
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => {
                        setOpen(false);
                      }}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  }
                >
                  {auth.userState.custom_message}
                </Alert>
              </Collapse>
            )}
            <Stack direction="row" alignItems="center">


              {(auth.userState.isAuthenticated &&
                <Stack direction='row' alignItems="center">
                  {(auth.userState.custom_message &&
                    <Box >
                      <IconButton aria-describedby='infoIcon'
                        onClick={() => {
                          setOpen(true);
                        }}>
                        < WarningIcon htmlColor="white" />
                      </IconButton>
                    </Box>
                  )}
                  <Box >
                    <List disablePadding>
                      <ListItem id='release-list' disablePadding >
                        <Tooltip title={
                          <Typography variant='caption' >
                            Release Notes
                          </Typography>}>
                          <ListItemButton id='release-button' primary={'v. ' + release.release[0].version + 'button'} variant="outlined" onClick={handleReleaseNote} sx={{ color: '#ffffff' }}>
                            <ListItemText color='white' id='release-title' primary={'v. ' + release.release[0].version} sx={{ textDecoration: 'underline' }} />
                          </ListItemButton>
                        </Tooltip>

                      </ListItem>
                    </List>
                    <Dialog
                      id='release-dialog'
                      //fullWidth={true}
                      maxWidth={'md'}
                      open={dialogOpen}
                      onClose={handleDialogClose}
                      fullWidth={true}
                    >

                      {release.release.map(objVersion =>

                        <Box key={objVersion.version + 'box'}>
                          <DialogContent key={objVersion.version + 'dialog'}>
                            <Typography variant='h5' color='primary' sx={{}}>
                              <b>{objVersion.version}</b>
                            </Typography>
                            <Typography variant='caption' color='neutral' sx={{ marginBottom: '0.5em' }}>
                              {objVersion.date}
                            </Typography>

                            <Box key={objVersion.version + 'subbox'}>
                              <TableContainer key={objVersion.version + 'tableCont'} component={Box} mt='0.5em' >
                                <Table key={objVersion.version + 'table'} size="small" >
                                  <TableBody key={objVersion.version + 'tablebody'}>
                                    {objVersion.note.map(objNote => (
                                      <TableRow key={objNote.title + 'row'} sx={{ '&:last-child td, &:last-child th': { border: 0 } }} >
                                        <TableCell key={objNote.title + 'cell'} width='1%' sx={{ border: 0 }}>{releaseIconMap[objNote.type.toLowerCase()]}</TableCell>
                                        <TableCell key={objNote.title + 'cellnote'} sx={{ border: 0 }}>
                                          <Typography variant='body1'>
                                            <b>{objNote.title}</b><br />
                                            {parseLinks(objNote.description)}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    )
                                    )}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          </DialogContent>
                          <Divider variant="middle" color='gray' />
                        </Box>
                      )}

                      <DialogActions>
                        <Button onClick={handleDialogClose} component={'label'}>Close</Button>
                      </DialogActions>
                    </Dialog>
                  </Box>

                  <Box>
                    <Tooltip title={
                      <Typography variant='caption' >
                        Knowledge Base
                      </Typography>}>
                      <IconButton aria-describedby='infoIcon' variant="contained" onClick={() => {
                        window.open("https://support.pagination.com/portal/en/kb/articles/web-application-how-to")
                      }}>
                        < HelpIcon htmlColor='white' />
                      </IconButton>
                    </Tooltip>
                  </Box>


                  <Box>
                    <Tooltip title={
                      <Typography variant='caption' >
                        Account
                      </Typography>}>
                      <IconButton aria-describedby='pino' variant="contained" onClick={handleClick}>
                        < AccountCircleIcon htmlColor='white' />
                      </IconButton>
                    </Tooltip>

                    <Popover
                      id='pino'
                      open={openPop}
                      anchorEl={anchorEl}
                      onClose={handleClose}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    >
                      <List>
                        <ListItem  >
                          <ListItemText primary={auth.userState.email} primaryTypographyProps={{ color: 'textSecondary' }} />
                        </ListItem>

                        <Divider variant="middle" color='gray' />

                        {(auth.userState.policy.canManagePaginationAPI || auth.userState.isAdmin) && (
                          <ListItem disablePadding >
                            <ListItemButton primary="Pagination API" variant="contained" onClick={() => {
                              redirect('/pagination-api')
                            }}>
                              <ListItemText primary="Pagination API" /* sx={{ color: Primarytheme.palette.primary.main }} */ />
                            </ListItemButton>
                          </ListItem>
                        )}


                        <ListItem disablePadding >
                          <ListItemButton primary="Change password" variant="contained" onClick={() => {
                            redirect('/changepassword')
                          }}>
                            <ListItemText primary="Change password" sx={{ color: 'black' }} />
                          </ListItemButton>
                        </ListItem>

                        <ListItem disablePadding >
                          <ListItemButton primary="Log out" variant="contained" onClick={handleLogOut}>
                            <ListItemText primary="Log out" sx={{ color: 'red' }} />
                          </ListItemButton>
                        </ListItem>

                      </List>
                    </Popover>
                  </Box>
                </Stack>
              )}


            </Stack>



          </Stack>
        </Stack>
      </Toolbar>
    </AppBar>
  );
}

DashboardHeader.propTypes = {
  logo: PropTypes.node,
  menuOpen: PropTypes.bool.isRequired,
  onToggleMenu: PropTypes.func.isRequired,
  title: PropTypes.string,
};

export default DashboardHeader;
